import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  Inject,
  OnChanges,
  SimpleChanges,
  OnInit,
} from '@angular/core';
import { get, toLower } from 'lodash';
import { DOCUMENT, NgClass, NgStyle } from '@angular/common';
import { v4 as uuidv4 } from 'uuid';
import { ToastrService } from 'ngx-toastr';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { DragDropDirective } from '../../../../directives/drag-drop.directive';
import { FileObject } from '../../../../models/file-object.model';
import { excludedFileFormats } from '../../../../constants/excluded-format';

interface ImageDimensions {
  width: number;
  height: number;
}

@Component({
  selector: 'app-upload-input',
  standalone: true,
  imports: [
    NgStyle,
    NgClass,
    DragDropDirective,
    ReactiveFormsModule,
    MatProgressBarModule,
  ],
  templateUrl: './upload-input.component.html',
  styleUrl: './upload-input.component.scss',
})
export class UploadInputComponent implements OnInit, OnChanges {
  private readonly defaultFileFormats = [
    'zip',
    'png',
    // 'svg',
    'docx',
    'pdf',
    'csv',
  ];
  private readonly excludedFileFormats = excludedFileFormats;

  @ViewChild('uploadInput', { static: true }) private uploadInput!: ElementRef;
  @Input() inputHeight?: string;

  // Validation for the uplaod input
  @Input() public minImgWidth = 0;
  @Input() public maxImgWidth = 0;
  @Input() public minImgHeight = 0;
  @Input() public maxImgHeight = 0;
  @Input() maxFileSizeAllowed: number;
  @Input() minFileSizeAllowed: number = 0;
  @Input() formatsAllowed!: string[];
  @Input() compressedImgWidth: number = 500;
  @Input() compressImg = true;

  // Error messages if any validation fails
  @Input() public fileGuideMesg = '';
  @Input() private minFileSizeError: string = '';
  @Input() private maxFileSizeError: string = '';
  @Input() private maxImgHeightError: string =
    'Image height exceeds the maximum allowed height';
  @Input() private minImgHeightError: string =
    'Image height is below the minimum required height';
  @Input() private maxImgWidthError: string =
    'Image width exceeds the maximum allowed width';
  @Input() private minImgWidthError: string =
    'Image width is below the minimum required width';
  @Input() private fileTypeError: string = '';
  @Input() resetInput: boolean = false;

  @Input() showDefaultPreview: boolean = false;
  @Input() showProgressBar: boolean = false;

  @Input({ required: true }) mainInputControl: FormControl = new FormControl();
  @Input({ required: true }) controlName: string = 'uploadInput';
  @Input() isMultiple = true;
  @Input() noOfFilesAllowed = 0;

  @Output() onFileUpload = new EventEmitter<any>();

  selectedImages: FileObject[] = [];
  @Input() lisOfProgressBarValue: {
    value: number;
    fileName: string;
    fileSize: string;
    uuid: string;
  }[] = [];
  totalUploads: number = 0;

  @Input() isContactUsForm = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private toaster: ToastrService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if ('resetInput' in changes && this.resetInput) {
      (<HTMLInputElement>this.uploadInput?.nativeElement).value = '';
    }

    if ('minFileSizeAllowed' in changes) {
      this.minFileSizeError = `File size must be atleast ${this.getFormattedFileSize(
        this.minFileSizeAllowed,
        1
      )}`;
    }

    if ('maxFileSizeAllowed' in changes) {
      this.maxFileSizeError = `File size cannot be greater than ${this.getFormattedFileSize(
        this.maxFileSizeAllowed,
        1
      )}`;
    }
    if ('maxImgHeight' in changes) {
      this.maxImgHeightError = `Image height exceeds the maximum allowed height of ${this.maxImgHeight}px.`;
    }

    if ('minImgHeight' in changes) {
      this.minImgHeightError = `Image height is below the minimum required height of ${this.minImgHeight}px.`;
    }

    if ('maxImgWidthError' in changes) {
      this.maxImgWidthError = `Image width exceeds the maximum allowed width of ${this.maxImgWidth}px.`;
    }

    if ('minImgWidth' in changes) {
      this.minImgWidthError = `Image width is below the minimum required width of ${this.minImgWidth}px.`;
    }

    if ('formatsAllowed' in changes) {
      this.formatsAllowed =
        changes['formatsAllowed'].currentValue || this.defaultFileFormats;
      this.fileTypeError = `The file format is not supported. Supported formats are: ${this.formatsAllowed.join(
        ', '
      )}.`;
    }
  }

  ngOnInit() {
    this.formatsAllowed ||= this.defaultFileFormats;
  }

  onFileSelected(event: Event | DragEvent) {
    let files: FileList | undefined;
    if (event instanceof DragEvent) {
      files = event.dataTransfer?.files;
    } else {
      files = (<HTMLInputElement>event.currentTarget).files || undefined;
    }

    if (!files?.length) {
      this.showErrorToaster('Error while fetching files');
      return;
    }
    this.totalUploads += files.length;
    if (
      this.noOfFilesAllowed > 0 &&
      this.totalUploads > this.noOfFilesAllowed
    ) {
      this.showErrorToaster(
        `You can upload a maximum of ${this.noOfFilesAllowed} files.`
      );

      this.totalUploads -= files.length;
      return;
    }
    if (!this.isMultiple && this.lisOfProgressBarValue.length === 1) {
      this.showErrorToaster('Only one file can be uploaded.');
      return;
    }

    Array.from(files).forEach((element: File) => {
      const uuid = uuidv4();
      this.lisOfProgressBarValue.push({
        value: 0,
        fileName: element.name,
        uuid: uuid,
        fileSize: this.getFormattedFileSize(element.size),
      });
      this.checkFileInWindow(element, uuid);
    });

    (<HTMLInputElement>this.uploadInput?.nativeElement).value = '';
  }

  onDeleteFile(imageData: FileObject) {
    this.selectedImages = this.selectedImages.filter(
      (image: FileObject) =>
        image.uniqueIdentifier !== imageData.uniqueIdentifier
    );

    this.toaster.success(`File ${imageData.fileName} removed successfully.`);
  }

  async checkFileInWindow(file: File, uuid: string) {
    const fileName = file?.name || '';

    // Check for hidden file names.
    if (!this.checkFileName(fileName)) {
      this.removeDoc(uuid);
      return;
    }

    const isFileDimensionValid = await this.checkFileDimension(file, uuid);
    const isValidFormat = this.checkFormatInWindow(fileName);
    const isValidFileSize = isValidFormat && this.checkFileSizeInWindow(file);

    if (isValidFormat && isValidFileSize && isFileDimensionValid) {
      this.updateBase64InWindow(file, fileName, uuid);
    } else {
      this.removeDoc(uuid);
    }
  }

  private checkFileName(fileName: string): boolean {
    if (!fileName) {
      this.showErrorToaster('Please select valid files.');
      return false;
    }

    // Ensure the file does not start with a dot
    if (fileName.startsWith('.')) {
      this.showErrorToaster(
        'Hidden files (starting with a dot) and files with invalid extensions are not allowed. Please select valid files.'
      );
      return false;
    }

    // Ensure the file does not have more than two dots (for file extensions)
    if (fileName.split('.').length > 2) {
      this.showErrorToaster(
        'Invalid filename. Please use a single file extension (e.g., .csv, .txt). Multiple extensions in same filename are not supported.'
      );
      return false;
    }

    return true;
  }

  removeDoc(uuid: string) {
    (<HTMLInputElement>this.uploadInput?.nativeElement).value = '';
    this.lisOfProgressBarValue = this.lisOfProgressBarValue.filter(
      (progress) => progress.uuid !== uuid
    );
    this.mainInputControl.setValue([], { emitEvent: false });
    this.onFileUpload.emit({});
  }

  updateBase64InWindow(file: File, fileName: string, uuid: string) {
    const { isImage, fileFormat } = this.checkFileType(file);
    let reader: FileReader = new FileReader();
    const selctedIndex = this.lisOfProgressBarValue.findIndex(
      (ele) => ele.uuid === uuid
    );

    reader.readAsDataURL(file);

    reader.onprogress = (progressData: ProgressEvent<FileReader>) => {
      if (progressData.lengthComputable) {
        this.lisOfProgressBarValue[selctedIndex].value = Math.round(
          (progressData.loaded * 100) / progressData.total
        );
      }
    };

    reader.onerror = (error) => {
      console.error(error);
    };

    reader.onloadend = () => {
      this.lisOfProgressBarValue[selctedIndex].value = 100; // Setting progress bar to fully loaded
      let result = (reader as any).result.toString();
      let miniBase64 = result.substring(
        result.indexOf('base64,') + 7,
        result.length
      );

      const fileSizeWithUnit = this.getFormattedFileSize(file.size);
      let compressedBase64 = result;

      if (isImage && this.compressImg) {
        const image = new Image();

        image.onload = () => {
          const { width, height } = image;
          const formattedFileFormat = fileFormat.substring(
            1,
            fileFormat.length
          );

          const canvas = this.document.createElement('canvas');
          const aspectRatio = this.compressedImgWidth / width;

          canvas.width = Math.min(this.compressedImgWidth, width);
          canvas.height = Math.min(height * aspectRatio, height);

          const canvasContext = <CanvasRenderingContext2D>(
            canvas.getContext('2d')
          );

          canvasContext?.drawImage(image, 0, 0, canvas.width, canvas.height);
          compressedBase64 = canvasContext.canvas.toDataURL(
            `image/${formattedFileFormat}`,
            0.8
          );

          this.selectedImages.push({
            base64: compressedBase64,
            fileName: fileName,
            uniqueIdentifier: uuid,
            size: fileSizeWithUnit,
          });

          this.setBase64ToFormControl(
            uuid,
            file,
            compressedBase64,
            miniBase64,
            fileName,
            file.size,
            file.type
          );
        };

        image.src = result;
      } else {
        this.selectedImages.push({
          base64: result,
          fileName: fileName,
          uniqueIdentifier: uuid,
          size: fileSizeWithUnit,
        });

        this.setBase64ToFormControl(
          uuid,
          file,
          result,
          miniBase64,
          fileName,
          file.size,
          file.type
        );
      }
    };
  }

  setBase64ToFormControl(
    uuid: string,
    rawfile: File | any,
    base64: string,
    miniBase64: string,
    fileName: string,
    fileSize: any,
    fileMimeType: string
  ) {
    const controlValue = [
      ...(this.isMultiple ? this.mainInputControl.value || [] : []),
      {
        uuid,
        controlName: this.controlName,
        base64,
        miniBase64,
        fileName,
        fileSize,
        fileMimeType,
        fileObject: rawfile,
      },
    ];

    this.mainInputControl.setValue(controlValue);
    this.onFileUpload.emit(controlValue);
  }

  checkFormatInWindow(fileName: string) {
    fileName = toLower(fileName);
    const allAllowedFormatList = this.formatsAllowed?.map((elm) => `.${elm}`);
    const isAnyFormatMatch = allAllowedFormatList?.length
      ? allAllowedFormatList.some((allowedFormat) =>
          fileName.endsWith(allowedFormat)
        )
      : true;

    const isExcludedFormat = this.excludedFileFormats.some((format) =>
      fileName.endsWith(`.${format}`)
    );

    // if (isExcludedFormat) {
    //   this.showErrorToaster('The file format is not supported.');
    // }

    if (!isAnyFormatMatch) {
      this.showErrorToaster(this.fileTypeError);
    }

    const result = isAnyFormatMatch && !isExcludedFormat;

    return result;
  }

  checkFileSizeInWindow(file: File, fileSize?: number) {
    const actualFileSize = this.getkbFromBytes(fileSize || file.size);
    if (actualFileSize > this.maxFileSizeAllowed) {
      this.showErrorToaster(this.maxFileSizeError);
      return false;
    } else if (actualFileSize < this.minFileSizeAllowed) {
      this.showErrorToaster(this.minFileSizeError);
      return false;
    }
    return true;
  }

  async checkFileDimension(file: File, uuid: string) {
    const fileBase64 = get(file, 'base64', null);
    const { isImage } = this.checkFileType(file);

    if (!isImage || !(this.maxImgWidth && this.maxImgHeight)) {
      return true;
    }

    let isDimensionValid = false;

    const checkImgWidth = (imgWidth: number) => {
      if (imgWidth <= this.maxImgWidth && imgWidth >= this.minImgWidth) {
        return true;
      }

      const widthErrMsg =
        imgWidth > this.maxImgWidth
          ? this.maxImgWidthError
          : this.minImgWidthError;

      this.showErrorToaster(widthErrMsg);
      this.removeDoc(uuid);
      return false;
    };

    const checkImgHeight = (imgHeight: number) => {
      if (imgHeight <= this.maxImgHeight && imgHeight >= this.minImgHeight) {
        return true;
      }

      const heightErrMsg =
        imgHeight > this.maxImgHeight
          ? this.maxImgHeightError
          : this.minImgHeightError;

      this.showErrorToaster(heightErrMsg);
      this.removeDoc(uuid);
      return false;
    };

    try {
      const { width, height } = await this.getImageDimensions(file);

      isDimensionValid = checkImgWidth(width) && checkImgHeight(height);
    } catch (error) {
      this.showErrorToaster(
        'Unable to check dimensions of the image. Try again later.'
      );
    } finally {
      return isDimensionValid;
    }
  }

  checkFileType(file: any) {
    let fileName = file.name;
    fileName = toLower(fileName);

    const imageFormats = ['.png', '.jpg', '.jpeg'];

    const isImage = imageFormats.some((allowedFormat) =>
      fileName.endsWith(allowedFormat)
    );
    const fileFormat = isImage
      ? imageFormats.find((format) => fileName.endsWith(format))
      : null;
    return { isImage: isImage, fileFormat: fileFormat };
  }

  onRemoveFile(uuid: string) {
    this.selectedImages = this.selectedImages.filter(
      (ele) => ele.uniqueIdentifier !== uuid
    );
    this.lisOfProgressBarValue = this.lisOfProgressBarValue.filter(
      (ele) => ele.uuid !== uuid
    );

    this.mainInputControl.setValue(
      this.mainInputControl.value?.filter((file: any) => file.uuid !== uuid)
    );
    (<HTMLInputElement>this.uploadInput?.nativeElement).value = '';
    this.totalUploads -= 1;
  }

  getImageDimensions(file: File) {
    return new Promise<ImageDimensions>(function (resolve, reject) {
      const imageObj = new Image();

      if (file instanceof File) {
        const url = URL.createObjectURL(file);
        imageObj.src = url;
      } else if (typeof file === 'string') {
        imageObj.src = file;
      } else {
        reject(
          new Error('Invalid input. Expected a File object or a URL string.')
        );
        return;
      }

      imageObj.onload = () => {
        resolve({ width: imageObj.width, height: imageObj.height });
        if (file instanceof File) {
          URL.revokeObjectURL(imageObj.src);
        }
      };

      imageObj.onerror = (error) => {
        reject(error);
        if (file instanceof File) {
          URL.revokeObjectURL(imageObj.src);
        }
      };
    });
  }

  public getkbFromBytes(sizeInBytes: number) {
    return sizeInBytes / 1000;
  }

  private getFormattedFileSize(
    fileSize: number,
    currentFileSizeIndex = 0
  ): string {
    const fileSizeUnit = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
    if (fileSize < 1000) {
      return `${fileSize.toFixed(2)} ${fileSizeUnit[currentFileSizeIndex]}`;
    }

    return this.getFormattedFileSize(fileSize / 1000, ++currentFileSizeIndex);
  }

  private showErrorToaster(errorMsg: string) {
    this.toaster.error(errorMsg);
  }

  get acceptFileMimes() {
    if (this.formatsAllowed.length) {
      return this.formatsAllowed.map((elm) => `.${elm}`).join(', ');
    } else {
      return '*';
    }
  }
}
